import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import paLogo from "@/assets/pa-logo.png";

interface SmartHeaderProps {
  onOpenReviews: () => void;
  onOpenServices: () => void;
  onScrollToFAQ: () => void;
  isOverlayOpen?: boolean;
  onGoToMain?: () => void;
}

export const SmartHeader = ({ 
  onOpenReviews, 
  onScrollToFAQ,
  isOverlayOpen = false,
  onGoToMain
}: SmartHeaderProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  const [isMobile, setIsMobile] = useState(false);

  // Check mobile once on mount and on resize - use RAF to batch reads
  useEffect(() => {
    let rafId: number;
    const checkMobile = () => {
      rafId = requestAnimationFrame(() => {
        setIsMobile(window.innerWidth < 768);
      });
    };
    checkMobile();
    window.addEventListener("resize", checkMobile, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Track if scrolled for mobile compact mode
      setIsScrolled(currentScrollY > 80);
      
      // On mobile, always show header
      if (isMobile) {
        setIsVisible(true);
        setLastScrollY(currentScrollY);
        return;
      }
      
      if (currentScrollY < lastScrollY || currentScrollY < 50) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY, isMobile]);

  // Always show header when overlay is open
  const showHeader = isOverlayOpen || isVisible;

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out",
        showHeader ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
      )}
    >
      {/* Desktop header */}
      <div 
        className="hidden md:flex items-center justify-between px-9 py-4"
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          backdropFilter: "blur(10px)",
        }}
      >
        <h2 className="text-sm font-medium tracking-[0.5px] text-white/85">
          PROPAGANDA AI
        </h2>
        
        <nav className="flex items-center gap-8">
          <button 
            onClick={onOpenReviews}
            className="relative text-[12px] font-medium tracking-[0.12em] text-white uppercase transition-colors duration-300 group"
          >
            <span>ПРОЕКТЫ</span>
            <span className="absolute left-0 bottom-0 w-full h-[1px] bg-white origin-left scale-x-0 transition-transform duration-300 ease-out group-hover:scale-x-100" />
          </button>
          {isOverlayOpen ? (
            <button 
              onClick={onGoToMain!}
              className="relative text-[12px] font-medium tracking-[0.12em] text-white uppercase transition-colors duration-300 group"
            >
              <span>ГЛАВНАЯ</span>
              <span className="absolute left-0 bottom-0 w-full h-[1px] bg-white origin-left scale-x-0 transition-transform duration-300 ease-out group-hover:scale-x-100" />
            </button>
          ) : (
            <button 
              onClick={onScrollToFAQ}
              className="relative text-[12px] font-medium tracking-[0.12em] text-white uppercase transition-colors duration-300 group"
            >
              <span>ВОПРОСЫ</span>
              <span className="absolute left-0 bottom-0 w-full h-[1px] bg-white origin-left scale-x-0 transition-transform duration-300 ease-out group-hover:scale-x-100" />
            </button>
          )}
        </nav>
      </div>

      {/* Mobile header - transforms to pill on scroll - BLACK and semi-transparent */}
      <div 
        className={cn(
          "md:hidden flex items-center justify-between transition-all duration-500 ease-out",
          isScrolled 
            ? "mx-auto mt-2 px-3 py-1.5 rounded-full max-w-[220px]" 
            : "px-4 py-2.5"
        )}
        style={{
          backgroundColor: isScrolled ? "rgba(20, 20, 20, 0.85)" : "rgba(0, 0, 0, 0.75)",
          border: isScrolled ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
          borderBottom: isScrolled ? "none" : "1px solid rgba(255, 255, 255, 0.08)",
          backdropFilter: "blur(12px)",
          boxShadow: isScrolled ? "0 4px 16px rgba(0, 0, 0, 0.25)" : "none",
        }}
      >
        {/* Logo - PA image when scrolled, text when not */}
        {isScrolled ? (
          <img 
            src={paLogo} 
            alt="PA" 
            className="w-5 h-5 object-contain transition-all duration-500"
          />
        ) : (
          <h2 className="text-[11px] font-medium tracking-[0.5px] text-white/85 transition-all duration-500">
            PROPAGANDA AI
          </h2>
        )}
        
        {/* Navigation */}
        <nav className="flex items-center gap-2.5">
          <button 
            onClick={onOpenReviews}
            className="relative text-[9px] font-medium tracking-[0.06em] uppercase transition-colors duration-300 text-white/90"
          >
            <span>ПРОЕКТЫ</span>
          </button>
          {isOverlayOpen ? (
            <button 
              onClick={onGoToMain!}
              className="relative text-[9px] font-medium tracking-[0.06em] uppercase transition-colors duration-300 text-white/90"
            >
              <span>ГЛАВНАЯ</span>
            </button>
          ) : (
            <button 
              onClick={onScrollToFAQ}
              className="relative text-[9px] font-medium tracking-[0.06em] uppercase transition-colors duration-300 text-white/90"
            >
              <span>ВОПРОСЫ</span>
            </button>
          )}
        </nav>
      </div>
    </header>
  );
};